import React, { useEffect, useMemo, useState } from 'react'
import {
  Button, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { patch, post } from '../../lib/request'
import {
  BENEFIT_CONTENT_SOURCE_LABELS,
  BENEFIT_DELIVERY_METHOD_LABELS,
  BenefitContentSource,
  BenefitPlan,
  BusinessFormat,
  RewardType,
  errMsg,
  fmtDate,
} from '../../lib/api'
import { ImageUploadButton, StatusTag, isFallbackPlan } from './shared'
import TierTable, { TierRow, fromTierRows, toTierRows, validateTierRows } from './TierTable'

const { Text } = Typography

/**
 * thankyou_v2（批次 8.2）：答谢方案表（两级列表的右栏）。
 *
 * 设计契约：planning/design-assets-v2/admin-benefit-config-two-level.html
 * - 行操作 = 编辑 | 复制 | 停用；兜底行（effectiveTo 为 null 且 published）显示绿底
 *   「兜底·不可停用」Tag 且停用按钮置灰（后端 findFallbackPlan 闸门为权威拦截）
 * - 「复制」= 读原行预填新增弹窗：名称加「（副本）」、生效期清空、状态 draft
 * - 表单扩 5 字段 + 分档子表（TierTable），提交 body 带 tiers[]
 *
 * 后端契约（benefit-plans.ts，只读核对）：
 * - GET /admin/benefit-plans 由父页统一持有（含 include tiers），本页只消费 plans prop
 * - POST/PATCH：name 禁词 400；published 生效期重叠 409（兜底兄弟豁免）；
 *   validateTiers 区间重叠/空档/首末档 400；兜底不可改 effectiveTo / 不可停用
 * - 删除 = 软删 PATCH { status: 'offline' }；无物理 DELETE 端点
 * - effectiveFrom/effectiveTo 为 Date（@db.Date）：提交 dayjs.format('YYYY-MM-DD')，回显 .slice(0,10)
 */
interface Props {
  canEdit: boolean
  /** 当前左栏选中的答谢类型（null = 全部） */
  rewardTypeId: number | null
  /** 类型 / 业态列表由父页统一持有（下拉选项只取 published） */
  types: RewardType[]
  formats: BusinessFormat[]
  /** 方案全量列表（父页拉取，含 tiers） */
  plans: BenefitPlan[]
  loading?: boolean
  /** 保存 / 停用后由父页重新 GET（左栏兜底计数同步刷新） */
  onChanged: () => void
}

interface PlanFormValue {
  rewardTypeId: number
  businessFormatId?: number | null
  name: string
  nameEn?: string
  referrerRewardText?: string
  referrerRewardTextEn?: string
  referredBenefitText?: string
  referredBenefitTextEn?: string
  /** thankyou_v2：礼遇交付方式（contract 随合同 / offline 线下） */
  benefitDeliveryMethod: 'contract' | 'offline'
  /** thankyou_v2：礼遇内容来源（unified 统一礼遇 / tiered 按面积分档） */
  benefitContentSource: BenefitContentSource
  /** thankyou_v2：现金答谢文字（standard/chamber 必填） */
  rewardCashText?: string
  rewardCashTextEn?: string
  /** thankyou_v2：实物答谢文字 */
  rewardInKindText?: string
  rewardInKindTextEn?: string
  tiers?: TierRow[]
  effectiveFrom?: dayjs.Dayjs | null
  effectiveTo?: dayjs.Dayjs | null
  coverUrl?: string
  sortOrder?: number
  status: 'draft' | 'published' | 'offline'
}

/**
 * 方案名禁词（前端预校验；后端 400 仍是权威闸门）。
 * thankyou_v2 与 Contract Pack「禁词表」对齐：zh 7 词作用于 name，en 3 词作用于 nameEn。
 */
const FORBIDDEN_PLAN_NAME_WORDS = ['奖励', '佣金', '返佣', '提成', '分佣', '酬金', '报酬']
const FORBIDDEN_PLAN_NAME_WORDS_EN = ['reward', 'commission', 'rebate']

/** 现金答谢必填的类型 code（Contract Pack：standard/chamber → rewardCashText 必填） */
const CASH_TEXT_REQUIRED_CODES = ['standard', 'chamber']

const PlanTab: React.FC<Props> = ({
  canEdit, rewardTypeId, types, formats, plans, loading, onChanged,
}) => {
  const [editing, setEditing] = useState<BenefitPlan | 'new' | 'copy' | null>(null)
  /** 复制来源行（仅 editing === 'copy' 时有值，用于预填） */
  const [copyFrom, setCopyFrom] = useState<BenefitPlan | null>(null)
  const [form] = Form.useForm<PlanFormValue>()
  const [saving, setSaving] = useState(false)

  const list = useMemo(
    () => (rewardTypeId == null ? plans : plans.filter((p) => p.rewardTypeId === rewardTypeId)),
    [plans, rewardTypeId],
  )

  const selectedType = useMemo(
    () => types.find((t) => t.id === rewardTypeId) ?? null,
    [types, rewardTypeId],
  )

  // benefitTiered 由 benefitContentSource 派生（只读展示，不单独编辑）
  const watchSource = Form.useWatch('benefitContentSource', form)
  const watchStatus = Form.useWatch('status', form)
  const watchTo = Form.useWatch('effectiveTo', form)
  const watchFrom = Form.useWatch('effectiveFrom', form)

  /** 编辑既有兜底方案时：effectiveTo 与「停用」状态锁定（后端 PATCH 400/409 双保险） */
  const editingFallback = editing !== null && editing !== 'new' && editing !== 'copy'
    && isFallbackPlan(editing)

  const openEdit = (p?: BenefitPlan) => {
    if (p) {
      setEditing(p)
      setCopyFrom(null)
      form.setFieldsValue({
        rewardTypeId: p.rewardTypeId,
        businessFormatId: p.businessFormatId ?? undefined,
        name: p.name,
        nameEn: p.nameEn ?? undefined,
        referrerRewardText: p.referrerRewardText ?? undefined,
        referrerRewardTextEn: p.referrerRewardTextEn ?? undefined,
        referredBenefitText: p.referredBenefitText ?? undefined,
        referredBenefitTextEn: p.referredBenefitTextEn ?? undefined,
        benefitDeliveryMethod: p.benefitDeliveryMethod ?? 'contract',
        benefitContentSource: p.benefitContentSource ?? (p.benefitTiered ? 'tiered' : 'unified'),
        rewardCashText: p.rewardCashText ?? undefined,
        rewardCashTextEn: p.rewardCashTextEn ?? undefined,
        rewardInKindText: p.rewardInKindText ?? undefined,
        rewardInKindTextEn: p.rewardInKindTextEn ?? undefined,
        tiers: toTierRows(p.tiers),
        // 后端 @db.Date 序列化为 ISO 字符串，回显取前 10 位日期部分
        effectiveFrom: p.effectiveFrom ? dayjs(String(p.effectiveFrom).slice(0, 10)) : null,
        effectiveTo: p.effectiveTo ? dayjs(String(p.effectiveTo).slice(0, 10)) : null,
        coverUrl: p.coverUrl ?? undefined,
        sortOrder: p.sortOrder ?? 0,
        status: p.status,
      })
    } else {
      setEditing('new')
      setCopyFrom(null)
      form.resetFields()
      // 新建默认落在当前左栏选中类型
      form.setFieldsValue({
        rewardTypeId: rewardTypeId ?? undefined,
        benefitDeliveryMethod: 'contract',
        benefitContentSource: 'unified',
        tiers: [],
      } as Partial<PlanFormValue> as PlanFormValue)
    }
  }

  /** 「复制」= 读原行预填新增弹窗（名称加「（副本）」、生效期清空、状态 draft） */
  const openCopy = (p: BenefitPlan) => {
    setEditing('copy')
    setCopyFrom(p)
    form.resetFields()
    form.setFieldsValue({
      rewardTypeId: p.rewardTypeId,
      businessFormatId: p.businessFormatId ?? undefined,
      name: `${p.name}（副本）`,
      nameEn: p.nameEn ? `${p.nameEn} (Copy)` : undefined,
      referrerRewardText: p.referrerRewardText ?? undefined,
      referrerRewardTextEn: p.referrerRewardTextEn ?? undefined,
      referredBenefitText: p.referredBenefitText ?? undefined,
      referredBenefitTextEn: p.referredBenefitTextEn ?? undefined,
      benefitDeliveryMethod: p.benefitDeliveryMethod ?? 'contract',
      benefitContentSource: p.benefitContentSource ?? (p.benefitTiered ? 'tiered' : 'unified'),
      rewardCashText: p.rewardCashText ?? undefined,
      rewardCashTextEn: p.rewardCashTextEn ?? undefined,
      rewardInKindText: p.rewardInKindText ?? undefined,
      rewardInKindTextEn: p.rewardInKindTextEn ?? undefined,
      tiers: toTierRows(p.tiers).map((t) => ({ ...t, id: undefined })),
      effectiveFrom: null,
      effectiveTo: null,
      coverUrl: p.coverUrl ?? undefined,
      sortOrder: p.sortOrder ?? 0,
      status: 'draft',
    } as PlanFormValue)
  }

  const save = async () => {
    const v = await form.validateFields()
    const tiers = v.tiers ?? []
    // tiered 时前端即时校验（重叠 / 空档 / 首末档），后端 validateTiers 为权威闸门
    if (v.benefitContentSource === 'tiered') {
      const errs = validateTierRows(tiers)
      if (errs.length > 0) {
        message.error(`分档子表校验未通过：${errs[0]}`)
        return
      }
    }
    const payload = {
      rewardTypeId: v.rewardTypeId,
      // allowClear 清空时传 null（后端 businessFormatId=null 表示不限业态）
      businessFormatId: v.businessFormatId ?? null,
      name: v.name.trim(),
      nameEn: v.nameEn?.trim() || null,
      referrerRewardText: v.referrerRewardText ?? null,
      referrerRewardTextEn: v.referrerRewardTextEn ?? null,
      referredBenefitText: v.referredBenefitText ?? null,
      referredBenefitTextEn: v.referredBenefitTextEn ?? null,
      // thankyou_v2 扩 5 字段 + tiers[]
      benefitDeliveryMethod: v.benefitDeliveryMethod,
      benefitContentSource: v.benefitContentSource,
      benefitTiered: v.benefitContentSource === 'tiered',
      rewardCashText: v.rewardCashText ?? null,
      rewardCashTextEn: v.rewardCashTextEn ?? null,
      rewardInKindText: v.rewardInKindText ?? null,
      rewardInKindTextEn: v.rewardInKindTextEn ?? null,
      tiers: v.benefitContentSource === 'tiered' ? fromTierRows(tiers) : [],
      effectiveFrom: v.effectiveFrom ? v.effectiveFrom.format('YYYY-MM-DD') : null,
      effectiveTo: v.effectiveTo ? v.effectiveTo.format('YYYY-MM-DD') : null,
      coverUrl: v.coverUrl?.trim() || null,
      sortOrder: v.sortOrder ?? 0,
      status: v.status,
    }
    setSaving(true)
    try {
      if (editing === 'new' || editing === 'copy') {
        await post('/admin/benefit-plans', payload)
        message.success(editing === 'copy' ? '已从原方案复制新建' : '答谢方案已新建')
      } else if (editing) {
        await patch(`/admin/benefit-plans/${editing.id}`, payload)
        message.success('答谢方案已更新')
      }
      setEditing(null)
      setCopyFrom(null)
      onChanged()
    } catch (err: unknown) {
      // 后端文案原样透出：禁词 400 / 生效期重叠 409 / 分档区间 400 / 兜底不可改期或停用 400
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const handleOffline = (r: BenefitPlan) => {
    patch(`/admin/benefit-plans/${r.id}`, { status: 'offline' })
      .then(() => { message.success('方案已停用'); onChanged() })
      .catch((err: unknown) => message.error(errMsg(err, '停用失败')))
  }

  const formatName = (r: BenefitPlan) => {
    if (r.businessFormatId == null) return <Text type="secondary">不限</Text>
    return r.businessFormat?.name ?? formats.find((f) => f.id === r.businessFormatId)?.name ?? '—'
  }

  const columns: ColumnsType<BenefitPlan> = [
    {
      title: '方案名称',
      dataIndex: 'name',
      key: 'name',
      render: (v: string, r) => (
        <Space size={6} wrap>
          <Text strong>{v}</Text>
          {/* 兜底标识（effectiveTo 为 null 且 published）：绿底 Tag + 停用置灰 */}
          {isFallbackPlan(r) && (
            <Tag color="success" style={{ background: '#EAF5ED', color: '#1A873B', borderColor: '#1A873B' }}>
              兜底·不可停用
            </Tag>
          )}
        </Space>
      ),
    },
    { title: '业态', key: 'businessFormat', width: 110, render: (_: unknown, r) => formatName(r) },
    {
      title: '礼遇来源',
      key: 'benefitContentSource',
      width: 130,
      render: (_: unknown, r) => (
        <Text style={{ fontSize: 12 }}>
          {BENEFIT_CONTENT_SOURCE_LABELS[r.benefitContentSource ?? (r.benefitTiered ? 'tiered' : 'unified')]}
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            {BENEFIT_DELIVERY_METHOD_LABELS[r.benefitDeliveryMethod ?? 'contract']}
          </Text>
        </Text>
      ),
    },
    {
      title: '生效期',
      key: 'effective',
      width: 200,
      // 兜底方案 = 生效期留空至长期，显示「起 → 长期」
      render: (_: unknown, r) =>
        r.effectiveFrom || r.effectiveTo
          ? `${fmtDate(r.effectiveFrom)} → ${r.effectiveTo ? fmtDate(r.effectiveTo) : '长期'}`
          : <Text type="secondary">长期（不限）</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (v: string) => <StatusTag status={v} />,
    },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          fixed: 'right' as const,
          width: 230,
          render: (_: unknown, r: BenefitPlan) => {
            const fallback = isFallbackPlan(r)
            const offlineDisabled = r.status === 'offline' || fallback
            return (
              <Space size={4} wrap>
                <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>编辑</Button>
                {/* 复制：读原行预填新增弹窗（名称加「（副本）」、生效期清空、状态 draft） */}
                <Button size="small" style={{ minHeight: 32 }} onClick={() => openCopy(r)}>复制</Button>
                {/* 无物理删除：删除语义 = 软删停用；兜底行置灰（后端闸门双保险） */}
                <Popconfirm
                  title="停用该答谢方案？"
                  description="停用后小程序端不再展示，核算回落上一级方案；历史记录仍读得出名称"
                  okText="停用"
                  cancelText="取消"
                  disabled={offlineDisabled}
                  onConfirm={() => handleOffline(r)}
                >
                  <Button
                    size="small"
                    danger
                    style={{ minHeight: 32 }}
                    disabled={offlineDisabled}
                    title={fallback ? '兜底方案不可停用' : undefined}
                  >
                    停用
                  </Button>
                </Popconfirm>
              </Space>
            )
          },
        }]
      : []),
  ]

  // 下拉选项只取 published（与后端「类型不存在或已停用 400」校验同口径）
  const typeOptions = types
    .filter((t) => t.status === 'published')
    .map((t) => ({ value: t.id, label: t.isDefault ? `${t.name}（兜底类型）` : t.name }))
  const formatOptions = formats
    .filter((f) => f.status === 'published')
    .map((f) => ({ value: f.id, label: f.name }))

  const modalTitle = editing === 'new'
    ? '新增答谢方案'
    : editing === 'copy'
      ? `复制答谢方案（原：${copyFrom?.name ?? ''}）`
      : '编辑答谢方案'

  // 当前编辑行所属类型 code（决定 rewardCashText 是否必填）
  const editingTypeCode = (() => {
    const tid = editing && editing !== 'new' && editing !== 'copy' ? editing.rewardTypeId : rewardTypeId
    return types.find((t) => t.id === tid)?.code
  })()
  const cashRequired = CASH_TEXT_REQUIRED_CODES.includes(editingTypeCode ?? '')

  const tierRows = Form.useWatch('tiers', form)
  const [tierErrs, setTierErrs] = useState<string[]>([])
  useEffect(() => {
    if (watchSource !== 'tiered') { setTierErrs([]); return }
    setTierErrs(validateTierRows(tierRows ?? []))
  }, [tierRows, watchSource])

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap align="center">
        <Text strong>
          方案列表{selectedType ? `（${selectedType.name}）` : '（全部类型）'}
        </Text>
        {canEdit && (
          <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
            + 新建方案
          </Button>
        )}
      </Space>
      <Table<BenefitPlan>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />
      <Modal
        title={modalTitle}
        open={!!editing}
        onOk={save}
        onCancel={() => { setEditing(null); setCopyFrom(null) }}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
        width={760}
      >
        {editingFallback && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            本行为该类型兜底方案：生效止须留空、不可停用（后端闸门拦截）。
          </Text>
        )}
        <Form<PlanFormValue>
          form={form}
          layout="vertical"
          initialValues={{
            sortOrder: 0,
            status: 'draft',
            benefitDeliveryMethod: 'contract',
            benefitContentSource: 'unified',
            tiers: [],
          }}
        >
          <Form.Item
            name="rewardTypeId"
            label="答谢类型"
            rules={[{ required: true, message: '请选择答谢类型' }]}
          >
            <Select placeholder="选择答谢类型（仅已发布项）" options={typeOptions} />
          </Form.Item>
          <Form.Item name="businessFormatId" label="业态（可清空 = 不限业态）">
            <Select
              allowClear
              placeholder="选择业态（仅已发布项，留空为不限）"
              options={formatOptions}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="方案名称"
            extra="答谢口径：方案名不得含「奖励 / 佣金 / 返佣 / 提成 / 分佣 / 酬金 / 报酬」（后端 400 为权威闸门）"
            rules={[
              { required: true, message: '方案名称必填' },
              { max: 100, message: '方案名不超过 100 字' },
              {
                validator: (_, value: string) => {
                  const hit = FORBIDDEN_PLAN_NAME_WORDS.find((w) => (value ?? '').includes(w))
                  return hit
                    ? Promise.reject(new Error(`方案名含禁词「${hit}」，请用「答谢」表述`))
                    : Promise.resolve()
                },
              },
            ]}
          >
            <Input placeholder="例：首月租金 10% 答谢" maxLength={100} showCount />
          </Form.Item>
          <Form.Item
            name="nameEn"
            label="英文名（选填）"
            rules={[
              { max: 100, message: '英文名不超过 100 字' },
              {
                validator: (_, value: string) => {
                  const hit = FORBIDDEN_PLAN_NAME_WORDS_EN.find((w) =>
                    (value ?? '').toLowerCase().includes(w))
                  return hit
                    ? Promise.reject(new Error(`英文名含禁词「${hit}」，请用 acknowledgement/benefit 表述`))
                    : Promise.resolve()
                },
              },
            ]}
          >
            <Input placeholder="e.g. 10% of first-month rent" maxLength={100} />
          </Form.Item>

          {/* thankyou_v2：礼遇交付方式 + 礼遇内容来源（benefitTiered 派生只读） */}
          <Space.Compact block>
            <Form.Item
              name="benefitDeliveryMethod"
              label="礼遇交付方式"
              style={{ width: '50%', marginRight: 8 }}
              rules={[{ required: true, message: '请选择礼遇交付方式' }]}
            >
              <Select
                options={[
                  { value: 'contract', label: '随合同' },
                  { value: 'offline', label: '线下' },
                ]}
              />
            </Form.Item>
            <Form.Item label="benefitTiered（由内容来源派生，只读）" style={{ width: '50%' }}>
              <Input
                readOnly
                value={watchSource === 'tiered' ? 'true' : 'false'}
                style={{ background: '#F5F7FA' }}
              />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name="benefitContentSource"
            label="礼遇内容来源"
            rules={[{ required: true, message: '请选择礼遇内容来源' }]}
          >
            <Radio.Group
              options={[
                { value: 'unified', label: '统一礼遇' },
                { value: 'tiered', label: '按面积分档' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="rewardCashText"
            label={`推荐人答谢·现金（中文${cashRequired ? '，必填' : ''}）`}
            rules={cashRequired ? [{ required: true, message: '标准 / 商会类型现金答谢文字必填' }] : []}
          >
            <Input.TextArea rows={2} placeholder="例：首月租金 10% 答谢金（税前，代扣个税 20%）" />
          </Form.Item>
          <Form.Item name="rewardCashTextEn" label="推荐人答谢·现金（英文·选填）">
            <Input.TextArea rows={2} placeholder="Cash acknowledgement text (EN)" />
          </Form.Item>
          <Form.Item name="rewardInKindText" label="推荐人答谢·实物（中文·选填）">
            <Input.TextArea rows={2} placeholder="例：商会年卡一张（不并入应税所得）" />
          </Form.Item>
          <Form.Item name="rewardInKindTextEn" label="推荐人答谢·实物（英文·选填）">
            <Input.TextArea rows={2} placeholder="In-kind acknowledgement text (EN)" />
          </Form.Item>

          <Form.Item
            name="referrerRewardText"
            label="推荐人答谢（既有字段·中文）"
            extra="既有 referrerRewardText / referredBenefitText 保留不动；新字段为 V2.0 口径"
          >
            <Input.TextArea rows={2} placeholder="例：首月租金 10% 答谢金" />
          </Form.Item>
          <Form.Item name="referrerRewardTextEn" label="推荐人答谢（既有字段·英文）">
            <Input.TextArea rows={2} placeholder="Referrer reward text (EN)" />
          </Form.Item>
          <Form.Item
            name="referredBenefitText"
            label={`被推荐人礼遇（中文${watchSource === 'unified' ? '，统一礼遇时必填' : ''}）`}
            rules={watchSource === 'unified'
              ? [{ required: true, message: '统一礼遇时被推荐人礼遇文字必填' }]
              : []}
          >
            <Input.TextArea rows={2} placeholder="例：签约礼包 + 免租加赠 7 天" />
          </Form.Item>
          <Form.Item name="referredBenefitTextEn" label="被推荐人礼遇（英文·选填）">
            <Input.TextArea rows={2} placeholder="Referred benefit text (EN)" />
          </Form.Item>

          {/* 分档子表：仅 tiered 时内嵌显示（可增删行 + 前端即时校验重叠/空档） */}
          {watchSource === 'tiered' && (
            <Form.Item
              name="tiers"
              label="礼遇分档子表"
              rules={[{
                validator: () => {
                  const errs = validateTierRows((form.getFieldValue('tiers') as TierRow[]) ?? [])
                  return errs.length > 0
                    ? Promise.reject(new Error(errs[0]))
                    : Promise.resolve()
                },
              }]}
            >
              <TierTable disabled={!canEdit} />
            </Form.Item>
          )}
          {watchSource === 'tiered' && tierErrs.length > 0 && (
            <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: -12, marginBottom: 12 }}>
              分档校验：{tierErrs.join('；')}
            </Text>
          )}

          <Space.Compact block>
            <Form.Item
              name="effectiveFrom"
              label="生效起（选填）"
              style={{ width: '50%', marginRight: 8 }}
            >
              <DatePicker style={{ width: '100%', minHeight: 44 }} disabled={editingFallback} />
            </Form.Item>
            <Form.Item
              name="effectiveTo"
              label="生效止（留空 = 兜底长期方案）"
              style={{ width: '50%' }}
              extra={editingFallback ? '兜底方案不可改生效止' : undefined}
            >
              <DatePicker style={{ width: '100%', minHeight: 44 }} disabled={editingFallback} />
            </Form.Item>
          </Space.Compact>
          {/* 兜底定义前端提示：effectiveTo 留空 + published 即成为该类型唯一兜底 */}
          {!editingFallback && !watchTo && watchStatus === 'published' && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: -8, marginBottom: 12 }}>
              生效止留空且状态为已发布 → 本方案将成为该类型的兜底方案（同类型仅允许一个，已存在时后端 409）。
            </Text>
          )}
          {!editingFallback && watchFrom && !watchTo && watchStatus !== 'published' && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: -8, marginBottom: 12 }}>
              生效止留空的已发布方案即兜底方案；草稿状态不参与兜底判定。
            </Text>
          )}
          <Form.Item label="配图（选填）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="coverUrl" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或 /uploads/benefits/xxx.jpg" />
              </Form.Item>
              <ImageUploadButton
                text="上传配图"
                disabled={!canEdit}
                onUploaded={(url) => form.setFieldValue('coverUrl', url)}
              />
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              仅一张，上传成功将覆盖左侧 URL；也可直接粘贴外链
            </div>
          </Form.Item>
          <Form.Item name="sortOrder" label="排序值（越小越靠前）">
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            extra={editingFallback ? '兜底方案不可停用（后端 400 拦截）' : undefined}
          >
            <Select
              disabled={editingFallback}
              options={[
                { value: 'draft', label: '草稿' },
                { value: 'published', label: '已发布' },
                ...(editingFallback ? [] : [{ value: 'offline', label: '停用' }]),
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default PlanTab
